<template>
  <aside class="rb-auth-panel" :aria-label="$t('authUi.testimonialLabel')">
    <div class="rb-auth-panel-inner">
        <div class="rb-auth-logo-badge" :aria-label="$t('authUi.brand')">
        <img src="../../assets/logo/roobroo-wordmark.svg" alt="">
      </div>
      <section class="rb-auth-wheel" aria-roledescription="carousel">
        <h2 class="rb-auth-wheel-title">
          {{ $t('authUi.testimonialTitle') }}<br>
          {{ $t('authUi.testimonialTitleBrand') }}
        </h2>
        <p class="rb-auth-session">
          {{ $t('authUi.afterSessionPrefix') }}
          <span class="rb-auth-session-count">{{ quotes[index].after }}</span>
          {{ $t('authUi.afterSessionSuffix') }}
        </p>
        <div
            ref="track"
            class="rb-auth-track"
            tabindex="0"
            :class="{ 'is-dragging': drag.active }"
            @scroll="onScroll"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
            @mouseenter="paused = true"
            @mouseleave="onMouseLeave"
            @focusin="paused = true"
            @focusout="paused = false"
            @keydown="onKeydown"
        >
          <figure
              v-for="(quote, i) in quotes"
              :key="i"
              class="rb-auth-quote"
          >
            <blockquote>{{ quote.text }}</blockquote>
            <figcaption>{{ $t('authUi.anonymous') }}</figcaption>
          </figure>
        </div>
        <ul class="rb-auth-dots" aria-hidden="true">
          <li v-for="(quote, i) in quotes" :key="'dot-' + i">
            <button
                type="button"
                :aria-current="i === index ? 'true' : null"
                :aria-label="$t('authUi.quoteDot', { n: i + 1 })"
                @click="select(i)"
            ></button>
          </li>
        </ul>
      </section>
      <div class="rb-auth-accents" aria-hidden="true"><i></i><i></i><i></i></div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'AuthTestimonialPanel',
  data() {
    return {
      index: 0,
      autoId: null,
      paused: false,
      programmatic: false,
      drag: {
        active: false,
        startX: 0,
        scrollStart: 0,
      },
    }
  },
  computed: {
    quotes() {
      return [
        {after: this.$t('authUi.afterTwo'), text: this.$t('authUi.quote1')},
        {after: this.$t('authUi.afterThree'), text: this.$t('authUi.quote2')},
        {after: this.$t('authUi.afterFour'), text: this.$t('authUi.quote3')},
        {after: this.$t('authUi.afterSix'), text: this.$t('authUi.quote4')},
        {after: this.$t('authUi.afterOne'), text: this.$t('authUi.quote5')},
      ]
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.goTo(0, false)
      this.startAuto()
    })
  },
  beforeDestroy() {
    this.stopAuto()
  },
  methods: {
    startAuto() {
      this.stopAuto()
      this.autoId = window.setInterval(() => {
        if (!this.paused && !this.drag.active) {
          this.goTo(this.index + 1)
        }
      }, 4200)
    },
    stopAuto() {
      if (this.autoId) {
        window.clearInterval(this.autoId)
        this.autoId = null
      }
    },
    trackEl() {
      return this.$refs.track
    },
    scrollMax() {
      const track = this.trackEl()
      if (!track) {
        return 0
      }
      return Math.max(0, track.scrollWidth - track.clientWidth)
    },
    goTo(i, smooth) {
      const track = this.trackEl()
      if (!track) {
        return
      }
      const cards = track.querySelectorAll('.rb-auth-quote')
      if (!cards.length) {
        return
      }
      this.index = (i + cards.length) % cards.length
      const card = cards[this.index]
      let target = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2
      target = Math.max(0, Math.min(target, this.scrollMax()))
      this.programmatic = true
      track.scrollTo({left: target, behavior: smooth === false ? 'auto' : 'smooth'})
      window.setTimeout(() => {
        this.programmatic = false
      }, smooth === false ? 50 : 450)
    },
    nearestIndex() {
      const track = this.trackEl()
      if (!track) {
        return 0
      }
      const cards = track.querySelectorAll('.rb-auth-quote')
      const mid = track.scrollLeft + track.clientWidth / 2
      let best = 0
      let bestDist = Infinity
      cards.forEach((card, i) => {
        const c = card.offsetLeft + card.offsetWidth / 2
        const d = Math.abs(c - mid)
        if (d < bestDist) {
          bestDist = d
          best = i
        }
      })
      return best
    },
    onScroll() {
      if (this.programmatic || this.drag.active) {
        return
      }
      this.index = this.nearestIndex()
    },
    select(i) {
      this.paused = true
      this.goTo(i)
      window.setTimeout(() => {
        this.paused = false
      }, 4000)
    },
    onPointerDown(e) {
      const track = this.trackEl()
      if (!track) {
        return
      }
      this.drag.active = true
      this.drag.startX = e.clientX
      this.drag.scrollStart = track.scrollLeft
      track.setPointerCapture(e.pointerId)
      this.paused = true
    },
    onPointerMove(e) {
      if (!this.drag.active) {
        return
      }
      const track = this.trackEl()
      if (!track) {
        return
      }
      track.scrollLeft = this.drag.scrollStart - (e.clientX - this.drag.startX)
    },
    onPointerUp(e) {
      if (!this.drag.active) {
        return
      }
      this.drag.active = false
      const track = this.trackEl()
      if (track) {
        try {
          track.releasePointerCapture(e.pointerId)
        } catch (err) {
          /* ignore */
        }
      }
      this.goTo(this.nearestIndex())
      window.setTimeout(() => {
        this.paused = false
      }, 2800)
    },
    onMouseLeave() {
      if (!this.drag.active) {
        this.paused = false
      }
    },
    onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.paused = true
        this.goTo(this.index + 1)
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        this.paused = true
        this.goTo(this.index - 1)
      }
    },
  },
}
</script>
